import React, { useState } from 'react';
import { db } from '../firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';
import { BrainCircuit, Search, Save, Power, Zap, Store, Loader2, Edit2, AlertCircle } from 'lucide-react';

const AdminSeoController = ({ tenants }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [editingKeywords, setEditingKeywords] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [triggeringId, setTriggeringId] = useState(null);

    const filteredTenants = tenants.filter(t => 
        (t.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (t.whatsappId || '').includes(searchTerm)
    );

    // Toggle Auto SEO for a client
    const toggleAutoSeo = async (clientId, currentStatus) => {
        if (!window.confirm(`Kya aap is client ka Auto-SEO ${currentStatus ? 'OFF' : 'ON'} karna chahte hain?`)) return;
        try {
            await updateDoc(doc(db, "clients", clientId), { autoSeoEnabled: !currentStatus });
        } catch (error) {
            alert("Error toggling Auto SEO: " + error.message);
        }
    };

    // Save edited keywords
    const saveKeywords = async (clientId) => {
        const keywordsString = editingKeywords[clientId];
        if (keywordsString === undefined) return; // Nothing changed

        setSavingId(clientId);
        try {
            // Convert comma separated string to array
            const keywordsArray = keywordsString.split(',').map(k => k.trim()).filter(k => k !== "");
            await updateDoc(doc(db, "clients", clientId), { targetKeywords: keywordsArray });
            
            // Remove from editing state
            const newEditing = { ...editingKeywords };
            delete newEditing[clientId];
            setEditingKeywords(newEditing);
            
            alert("✅ Keywords Saved!");
        } catch (error) {
            alert("Error saving keywords: " + error.message);
        }
        setSavingId(null);
    };

    // Force Trigger AI Backend
    const forceTriggerSEO = async (clientId) => {
        if (!window.confirm("Is client ke liye abhi live GMB SEO Post generate karna chahte hain?")) return;
        setTriggeringId(clientId);
        try {
            // Note: You need to create this HTTP trigger in your Firebase functions
            const response = await fetch(`https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/forceRunAutoSeo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clientId })
            });
            const data = await response.json();
            if (data.success) {
                alert("🎉 Success! Post generated and pushed to GMB.");
            } else {
                alert("❌ Error: " + data.error);
            }
        } catch (error) {
            alert("❌ Network Error: " + error.message);
        }
        setTriggeringId(null);
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in">
            {/* Header */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-xl md:text-2xl font-black text-slate-900 flex items-center gap-2 uppercase tracking-tight">
                        <BrainCircuit className="text-indigo-600" /> Master Auto-SEO Engine
                    </h2>
                    <p className="text-sm font-medium text-slate-500 mt-1">Control AI generation & GMB pushing for all tenants.</p>
                </div>
                <div className="relative w-full md:w-72">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input 
                        type="text" 
                        placeholder="Search clients..." 
                        value={searchTerm} 
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:border-indigo-500 transition-all"
                    />
                </div>
            </div>

            {/* List */}
            <div className="grid grid-cols-1 gap-4">
                {filteredTenants.map(client => {
                    const isEditing = editingKeywords[client.id] !== undefined;
                    const keywordsList = client.targetKeywords || [];
                    const displayKeywords = isEditing ? editingKeywords[client.id] : keywordsList.join(', ');
                    const isGmbConnected = client.gmbLocationName && (client.gmbRefreshToken || client.googleRefreshToken);

                    return (
                        <div key={client.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:border-indigo-200 transition-all">
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                                
                                {/* Info */}
                                <div className="flex items-start gap-4 w-full lg:w-1/3">
                                    <div className={`p-3 rounded-xl ${client.autoSeoEnabled ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
                                        <Store size={24} />
                                    </div>
                                    <div>
                                        <h3 className="font-black text-lg text-slate-900 leading-tight">{client.name}</h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[10px] font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-600 border border-slate-200">
                                                {client.whatsappId}
                                            </span>
                                            {isGmbConnected ? (
                                                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded uppercase tracking-wider">GMB Linked</span>
                                            ) : (
                                                <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded uppercase tracking-wider flex items-center gap-1"><AlertCircle size={10}/> GMB Missing</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Keywords Editor */}
                                <div className="w-full lg:w-1/3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex justify-between items-center">
                                        Target SEO Keywords
                                        {!isEditing && (
                                            <button onClick={() => setEditingKeywords({...editingKeywords, [client.id]: displayKeywords})} className="text-indigo-500 hover:text-indigo-700 flex items-center gap-1">
                                                <Edit2 size={12} /> Edit
                                            </button>
                                        )}
                                    </label>
                                    
                                    {isEditing ? (
                                        <div className="flex gap-2">
                                            <input 
                                                type="text" 
                                                value={displayKeywords} 
                                                onChange={(e) => setEditingKeywords({...editingKeywords, [client.id]: e.target.value})}
                                                placeholder="Python in Dehradun, Best Institute..." 
                                                className="w-full p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg text-sm font-bold text-indigo-900 outline-none focus:ring-2 focus:ring-indigo-300"
                                            />
                                            <button 
                                                onClick={() => saveKeywords(client.id)}
                                                disabled={savingId === client.id}
                                                className="bg-indigo-600 text-white p-2.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                            >
                                                {savingId === client.id ? <Loader2 size={18} className="animate-spin"/> : <Save size={18} />}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg text-sm font-medium text-slate-600 min-h-[44px] max-h-[110px] overflow-y-auto custom-scrollbar">
                                            {keywordsList.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {keywordsList.map((kw, i) => (
                                                        <span key={i} className="bg-white border border-slate-200 px-2 py-1 rounded text-[11px] font-bold shadow-sm">{kw}</span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-slate-400 italic text-[11px]">No keywords set...</span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="w-full lg:w-auto flex flex-row lg:flex-col gap-2 justify-end">
                                    <button 
                                        onClick={() => toggleAutoSeo(client.id, client.autoSeoEnabled)}
                                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-black text-[11px] uppercase tracking-widest border transition-all ${
                                            client.autoSeoEnabled 
                                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' 
                                            : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                                        }`}
                                    >
                                        <Power size={14} className={client.autoSeoEnabled ? 'text-emerald-500' : ''} />
                                        {client.autoSeoEnabled ? 'Auto-SEO ON' : 'Auto-SEO OFF'}
                                    </button>

                                    <button 
                                        onClick={() => forceTriggerSEO(client.id)}
                                        disabled={!isGmbConnected || triggeringId === client.id || keywordsList.length === 0}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-black text-[11px] uppercase tracking-widest bg-slate-900 text-white hover:bg-black disabled:opacity-40 disabled:hover:bg-slate-900 transition-all shadow-md"
                                        title={!isGmbConnected ? "GMB API Not Connected" : keywordsList.length === 0 ? "Set Keywords First" : "Generate & Post Now"}
                                    >
                                        {triggeringId === client.id ? <Loader2 size={14} className="animate-spin"/> : <Zap size={14} className="text-amber-400"/>}
                                        Force Post Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AdminSeoController;